<template>
  <div class="crud_form_wrapper user_subscription_details">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.subscription_details") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #f7941e">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Loading -->
    <div v-if="loading" class="text-center py-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <!-- End:: Loading -->

    <div v-else class="single_step_form_content_wrapper">
      <!-- Start:: User Data Section -->
      <div class="details_section mb-4">
        <h5 class="section_title">
          <i class="fal fa-user"></i>
          {{ $t("TITLES.user_data") }}
        </h5>
        <div class="row">
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{ $t("PLACEHOLDERS.full_name") }}</span>
              <span class="value">{{ subscriptionData.full_name || "-" }}</span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{
                $t("PLACEHOLDERS.identity_number")
              }}</span>
              <span class="value">{{
                subscriptionData.national_id || "-"
              }}</span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{ $t("PLACEHOLDERS.phone") }}</span>
              <span class="value">{{ subscriptionData.mobile || "-" }}</span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{ $t("PLACEHOLDERS.email") }}</span>
              <span class="value">{{ subscriptionData.email || "-" }}</span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{ $t("PLACEHOLDERS.city") }}</span>
              <span class="value">{{ subscriptionData.city || "-" }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- End:: User Data Section -->

      <!-- Start:: Subscription Data Section -->
      <div class="details_section mb-4">
        <h5 class="section_title">
          <i class="fal fa-credit-card"></i>
          {{ $t("TITLES.subscription_data") }}
        </h5>
        <div class="row">
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{
                $t("PLACEHOLDERS.subscription_type")
              }}</span>
              <span class="value">{{ $t("PLACEHOLDERS.monthly") }}</span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{
                $t("PLACEHOLDERS.subscription_status")
              }}</span>
              <v-chip
                :color="getStatusColor(subscriptionData.status)"
                text-color="white"
                small
              >
                {{ getStatusText(subscriptionData.status) }}
              </v-chip>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{
                $t("PLACEHOLDERS.subscription_start_date")
              }}</span>
              <span class="value">{{
                formatDate(subscriptionData.start_date)
              }}</span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{
                $t("PLACEHOLDERS.subscription_end_date")
              }}</span>
              <span class="value">{{
                formatDate(subscriptionData.end_date)
              }}</span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{ $t("PLACEHOLDERS.remaining_days") }}</span>
              <span
                class="value"
                :class="getRemainingDaysClass(subscriptionData.remaining_days)"
              >
                {{ subscriptionData.remaining_days }}
                {{ $t("PLACEHOLDERS.days") }}
              </span>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 mb-3">
            <div class="info_card">
              <span class="label">{{
                $t("PLACEHOLDERS.subscription_price")
              }}</span>
              <span class="value"
                >{{ subscriptionData.price }} {{ $t("PLACEHOLDERS.sar") }}</span
              >
            </div>
          </div>
          <div
            class="col-md-4 col-sm-6 mb-3"
            v-if="subscriptionData.extension_days"
          >
            <div class="info_card">
              <span class="label">{{ $t("PLACEHOLDERS.extension_days") }}</span>
              <span class="value"
                >{{ subscriptionData.extension_days }}
                {{ $t("PLACEHOLDERS.days") }}</span
              >
            </div>
          </div>
          <div
            class="col-md-4 col-sm-6 mb-3"
            v-if="subscriptionData.transaction_id"
          >
            <div class="info_card">
              <span class="label">{{ $t("PLACEHOLDERS.transaction_id") }}</span>
              <span class="value">{{ subscriptionData.transaction_id }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- End:: Subscription Data Section -->

      <!-- Start:: Admin Actions Section -->
      <div class="details_section mb-4">
        <h5 class="section_title">
          <i class="fal fa-cogs"></i>
          {{ $t("TITLES.admin_actions") }}
        </h5>
        <div class="row">
          <div class="col-md-6 mb-3">
            <v-btn
              color="success"
              class="action_btn"
              @click="openExtendModal"
              :disabled="subscriptionData.status === 'cancelled'"
            >
              <i class="fal fa-calendar-plus me-2"></i>
              {{ $t("BUTTONS.extend_subscription") }}
            </v-btn>
          </div>
          <div class="col-md-6 mb-3">
            <v-btn
              color="error"
              class="action_btn"
              @click="openDeactivateModal"
              :disabled="
                subscriptionData.status === 'cancelled' ||
                subscriptionData.status === 'expired'
              "
            >
              <i class="fal fa-ban me-2"></i>
              {{ $t("BUTTONS.deactivate_subscription") }}
            </v-btn>
          </div>
        </div>
      </div>
      <!-- End:: Admin Actions Section -->

      <!-- Start:: Previous Subscriptions Section -->
      <div
        class="details_section"
        v-if="otherSubscriptions && otherSubscriptions.length > 0"
      >
        <h5 class="section_title">
          <i class="fal fa-history"></i>
          {{ $t("TITLES.previous_subscriptions") }}
        </h5>
        <v-data-table
          :headers="previousSubscriptionsHeaders"
          :items="otherSubscriptions"
          hide-default-footer
          class="previous_subscriptions_table"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>
          <template v-slot:[`item.price`]="{ item }">
            {{ item.price }} {{ $t("PLACEHOLDERS.sar") }}
          </template>
          <template v-slot:[`item.start_date`]="{ item }">
            {{ formatDate(item.start_date) }}
          </template>
          <template v-slot:[`item.end_date`]="{ item }">
            {{ formatDate(item.end_date) }}
          </template>
        </v-data-table>
      </div>
      <!-- End:: Previous Subscriptions Section -->
    </div>

    <!-- Start:: Extend Subscription Modal -->
    <v-dialog v-model="extendModal" max-width="500px" persistent>
      <form>
        <v-card>
          <v-card-title class="headline">
            {{ $t("TITLES.extend_subscription") }}
          </v-card-title>
          <v-card-text>
            <div class="row">
              <base-input
                col="12"
                type="number"
                :placeholder="$t('PLACEHOLDERS.extension_days')"
                v-model="extensionData.days"
                required
                min="1"
              />
              <div class="col-12">
                <base-input
                  v-model="extensionData.reason"
                  :placeholder="$t('PLACEHOLDERS.extension_reason')"
                  outlined
                  rows="3"
                  type="textarea"
                  required
                ></base-input>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeExtendModal">
              {{ $t("BUTTONS.cancel") }}
            </v-btn>
            <v-btn
              color="success"
              @click="extendSubscription"
              :loading="isWaitingRequest"
              :disabled="!extensionData.days || !extensionData.reason"
            >
              {{ $t("BUTTONS.confirm") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </form>
    </v-dialog>
    <!-- End:: Extend Subscription Modal -->

    <!-- Start:: Deactivate Subscription Modal -->
    <v-dialog v-model="deactivateModal" max-width="500px" persistent>
      <form>
        <v-card>
          <v-card-title class="headline text-danger">
            {{ $t("TITLES.deactivate_subscription") }}
          </v-card-title>
          <v-card-text>
            <p class="mb-3">
              {{ $t("MESSAGES.deactivate_subscription_warning") }}
            </p>
            <div class="col-12">
              <base-input
                type="textarea"
                v-model="deactivationData.reason"
                :placeholder="$t('PLACEHOLDERS.deactivation_reason')"
                outlined
                rows="3"
                required
              ></base-input>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeDeactivateModal">
              {{ $t("BUTTONS.cancel") }}
            </v-btn>
            <v-btn
              color="error"
              @click="deactivateSubscription"
              :loading="isWaitingRequest"
              :disabled="!deactivationData.reason"
            >
              {{ $t("BUTTONS.confirm") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </form>
    </v-dialog>
    <!-- End:: Deactivate Subscription Modal -->
  </div>
</template>

<script>
export default {
  name: "ShowUserSubscription",

  data() {
    return {
      loading: false,
      isWaitingRequest: false,

      subscriptionData: {
        id: null,
        user_id: null,
        full_name: null,
        national_id: null,
        mobile: null,
        email: null,
        city: null,
        start_date: null,
        end_date: null,
        remaining_days: null,
        status: null,
        price: null,
        extension_days: null,
        transaction_id: null,
      },

      otherSubscriptions: [],

      previousSubscriptionsHeaders: [
        {
          text: this.$t("PLACEHOLDERS.subscription_id"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.start_date"),
          value: "start_date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.end_date"),
          value: "end_date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.amount_paid"),
          value: "price",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.extension_days"),
          value: "extension_days",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_status"),
          value: "status",
          align: "center",
          sortable: false,
        },
      ],

      // Modals
      extendModal: false,
      deactivateModal: false,

      extensionData: {
        days: null,
        reason: null,
      },

      deactivationData: {
        reason: null,
      },
    };
  },

  methods: {
    // Start:: Get Status Color
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "success";
        case "expired":
          return "error";
        case "pending_renewal":
          return "warning";
        case "cancelled":
          return "grey";
        default:
          return "primary";
      }
    },
    // End:: Get Status Color

    // Start:: Get Status Text
    getStatusText(status) {
      switch (status) {
        case "active":
          return this.$t("PLACEHOLDERS.active_subscription");
        case "inactivated_by_admin":
          return this.$t("PLACEHOLDERS.cancelled_subscription");
        case "expired":
          return this.$t("PLACEHOLDERS.expired_subscription");
        case "pending_renewal":
          return this.$t("PLACEHOLDERS.pending_renewal");
        case "cancelled":
          return this.$t("PLACEHOLDERS.cancelled_subscription");
        default:
          return status;
      }
    },
    // End:: Get Status Text

    // Start:: Get Remaining Days Class
    getRemainingDaysClass(days) {
      if (days <= 3) return "text-danger font-weight-bold";
      if (days <= 7) return "text-warning font-weight-bold";
      return "text-success";
    },
    // End:: Get Remaining Days Class

    // Start:: Format Date
    formatDate(dateString) {
      if (!dateString) return "-";
      return dateString.split(" ")[0].split("T")[0];
    },
    // End:: Format Date

    // Start:: Get Subscription Data
    async getSubscriptionData() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `subscriptions-details/show/${this.$route.params.id}`,
        });

        const data = res.data.data.details;
        this.subscriptionData = {
          id: data.id,
          user_id: data?.user?.id,
          full_name: data.user?.name,
          national_id: data.user?.nationalId,
          mobile: data.user?.mobile,
          email: data.user?.email,
          city: data.user?.city_name || null,
          start_date: data.start_date,
          end_date: data.end_date,
          remaining_days: data.total_extensions_days,
          status: data.status,
          price: data.price,
          extension_days: data.extension_days || null,
          transaction_id: data.transaction_id || null,
        };

        // Handle other subscriptions
        const others = res.data.data.other_subscriptions;
        if (others && Array.isArray(others)) {
          this.otherSubscriptions = others;
        } else if (
          others &&
          typeof others === "object" &&
          Object.keys(others).length > 0
        ) {
          this.otherSubscriptions = Object.values(others);
        } else {
          this.otherSubscriptions = [];
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error.response.message);
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Get Subscription Data

    // Start:: Extend Modal Methods
    openExtendModal() {
      this.extendModal = true;
    },
    closeExtendModal() {
      this.extendModal = false;
      this.extensionData = { days: null, reason: null };
    },
    async extendSubscription() {
      this.isWaitingRequest = true;
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("extention_days", this.extensionData.days);
      REQUEST_DATA.append("extention_reason", this.extensionData.reason);

      try {
        await this.$axios({
          method: "POST",
          url: `subscriptions/extention/${this.$route.params.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(
          this.$t("MESSAGES.subscription_extended_successfully")
        );
        this.closeExtendModal();
        this.getSubscriptionData();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Extend Modal Methods

    // Start:: Deactivate Modal Methods
    openDeactivateModal() {
      this.deactivateModal = true;
    },
    closeDeactivateModal() {
      this.deactivateModal = false;
      this.deactivationData = { reason: null };
    },
    async deactivateSubscription() {
      this.isWaitingRequest = true;
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("deactivation_reason", this.deactivationData.reason);

      try {
        await this.$axios({
          method: "POST",
          url: `subscriptions/deactivate/${this.$route.params.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(
          this.$t("MESSAGES.subscription_deactivated_successfully")
        );
        this.closeDeactivateModal();
        this.getSubscriptionData();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Deactivate Modal Methods
  },

  created() {
    this.getSubscriptionData();
  },
};
</script>

<style lang="scss" scoped>
.user_subscription_details {
  .details_section {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .section_title {
      color: #f7941e;
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 2px solid #f7941e;

      i {
        margin-inline-end: 8px;
      }
    }

    .info_card {
      background: #f8f9fa;
      border-radius: 6px;
      padding: 15px;
      height: 100%;

      .label {
        display: block;
        font-size: 13px;
        color: #6c757d;
        margin-bottom: 5px;
      }

      .value {
        display: block;
        font-size: 15px;
        font-weight: 600;
        color: #333;
      }
    }

    .action_btn {
      width: 100%;
      height: 45px;
      font-size: 14px;
    }
  }

  .previous_subscriptions_table {
    margin-top: 15px;
  }
}
</style>
